import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box, useToast, useDisclosure, AvatarBadge, Avatar, Tag, Badge, IconButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  HStack, VStack, Text,
} from "@chakra-ui/react";
import { AddIcon, SearchIcon } from "@chakra-ui/icons";
import EmojiPicker from "emoji-picker-react";
import { api, API_BASE } from "../../lib/api";
import { getSocket } from "../../lib/socket";
import { useShellHeader } from "../../Components/AppShell";
import PollMessage from "./PollMessage";
import PollCreateModal from "./PollCreateModal";
import Attachments from "./Attachments";
import ThreadDrawer from "./ThreadDrawer";
import ChannelInfoModal from "./ChannelInfoModal";
import ChatSearchModal from "./ChatSearchModal";

const QUICK_REACTIONS = ["👍", "❤️", "🎉", "🚀", "👀", "🙏"];

function fmtTime(d) {
  return new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function channelLabel(ch, meId) {
  if (ch.kind === "general") return "# general";
  if (ch.kind === "project") return `# ${ch.project?.name || ch.name || "project"}`;
  if (ch.kind === "dept") return `# ${ch.name || "dept"}`;
  if (ch.kind === "dm") {
    const other = (ch.members || []).find((m) => String(m._id) !== String(meId));
    return other ? other.username : "DM";
  }
  return ch.name || "channel";
}

export default function ChatPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const meId = localStorage.getItem("userID");

  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(params.get("c") || null);
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [text, setText] = useState("");
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [onlineSet, setOnlineSet] = useState(new Set());
  const [typingNames, setTypingNames] = useState([]);
  const [users, setUsers] = useState([]);
  const [hoverId, setHoverId] = useState(null);
  const [reactionMenuFor, setReactionMenuFor] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [showPinned, setShowPinned] = useState(true);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [collapsedProjects, setCollapsedProjects] = useState(new Set());

  const dmModal = useDisclosure();
  const pollModal = useDisclosure();
  const threadDisc = useDisclosure();
  const infoDisc = useDisclosure();
  const searchDisc = useDisclosure();
  const [openThreadParent, setOpenThreadParent] = useState(null);
  const [channelPrefs, setChannelPrefs] = useState({});
  const [readers, setReaders] = useState([]); // [{user:{_id,username,profileImage}, lastSeenAt}]
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimers = useRef({});
  const composerTypingRef = useRef(null);
  const activeIdRef = useRef(activeId);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  useShellHeader({ title: "Chat" }, []);

  // Cmd/Ctrl+K to open search
  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchDisc.onOpen();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, []);

  const activeChannel = useMemo(
    () => channels.find((c) => String(c._id) === String(activeId)) || null,
    [channels, activeId]
  );

  // ---------- Loaders ----------
  async function loadChannels() {
    setLoadingChannels(true);
    try {
      const d = await api("/chat/channels");
      // Phase 16: only show general / project / dept / dm channels.
      const filtered = (d.channels || []).filter((c) => c.kind !== "task");
      setChannels(filtered);
      if (!activeId && filtered.length) {
        const first = filtered[0]._id;
        setActiveId(first);
        setParams({ c: first });
      }
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoadingChannels(false); }
  }
  async function loadMessages(chId) {
    if (!chId) return;
    setLoadingMessages(true);
    try {
      const [d, p] = await Promise.all([
        api(`/chat/channels/${chId}/messages?limit=100`),
        api(`/chat/channels/${chId}/pinned`).catch(() => ({ pinned: [] })),
      ]);
      setMessages(d.messages || []);
      setPinned(p.pinned || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "auto" }), 50);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoadingMessages(false); }
  }
  async function loadUsers() {
    try { const d = await api("/chat/users"); setUsers(d.users || []); } catch (e) {}
  }
  async function loadPrefs() {
    try { const d = await api("/chat/me/channel-prefs"); setChannelPrefs(d.prefs || {}); } catch (e) {}
  }

  const [connectionState, setConnectionState] = useState("connected");

  useEffect(() => {
    loadChannels();
    loadUsers();
    loadPrefs();
    const socket = getSocket();
    if (!socket) return;

    function onDisconnect() { setConnectionState("disconnected"); }
    function onReconnectAttempt() { setConnectionState("reconnecting"); }
    function onConnect() {
      setConnectionState("connected");
      // resync messages for active channel on reconnect
      if (activeIdRef.current) {
        api(`/chat/channels/${activeIdRef.current}/messages?limit=100`)
          .then((d) => setMessages(d.messages || []))
          .catch(() => {});
      }
    }
    socket.on("disconnect", onDisconnect);
    socket.on("reconnect_attempt", onReconnectAttempt);
    socket.on("connect", onConnect);

    function onInitialPresence({ online }) { setOnlineSet(new Set(online.map(String))); }
    function onOnline({ userId }) { setOnlineSet((p) => new Set(p).add(String(userId))); }
    function onOffline({ userId }) {
      setOnlineSet((p) => { const n = new Set(p); n.delete(String(userId)); return n; });
    }
    function onMessage({ channelId, message }) {
      if (String(channelId) !== String(activeIdRef.current)) {
        setChannels((cs) => cs.map((c) =>
          String(c._id) === String(channelId)
            ? { ...c, unread: (c.unread || 0) + (String(message.author?._id || message.author) !== String(meId) ? 1 : 0), lastMessageAt: message.createdAt }
            : c
        ));
        return;
      }
      setMessages((prev) => prev.some((m) => String(m._id) === String(message._id)) ? prev : [...prev, message]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    }
    function onMessageEdit({ channelId, message }) {
      if (String(channelId) !== String(activeIdRef.current)) return;
      setMessages((prev) => prev.map((m) => String(m._id) === String(message._id) ? message : m));
      setPinned((prev) => prev.map((m) => String(m._id) === String(message._id) ? { ...m, ...message } : m));
    }
    function onMessageDelete({ channelId, messageId }) {
      if (String(channelId) !== String(activeIdRef.current)) return;
      setMessages((prev) => prev.map((m) => String(m._id) === String(messageId) ? { ...m, deletedAt: new Date() } : m));
      setPinned((prev) => prev.filter((m) => String(m._id) !== String(messageId)));
    }
    function onReaction({ channelId, messageId, reactions }) {
      if (String(channelId) !== String(activeIdRef.current)) return;
      setMessages((prev) => prev.map((m) => String(m._id) === String(messageId) ? { ...m, reactions } : m));
    }
    function onPin({ channelId, messageId, pinned: isPinned }) {
      if (String(channelId) !== String(activeIdRef.current)) return;
      setMessages((prev) => prev.map((m) => String(m._id) === String(messageId) ? { ...m, pinned: isPinned, pinnedAt: isPinned ? new Date().toISOString() : null } : m));
      api(`/chat/channels/${activeIdRef.current}/pinned`).then((d) => setPinned(d.pinned || [])).catch(() => {});
    }
    function onPollUpdate({ channelId, messageId, poll }) {
      if (String(channelId) !== String(activeIdRef.current)) return;
      setMessages((prev) => prev.map((m) => String(m._id) === String(messageId) ? { ...m, poll } : m));
    }
    function onTyping({ channelId, userId, username, typing }) {
      if (String(channelId) !== String(activeIdRef.current)) return;
      if (String(userId) === String(meId)) return;
      const key = `${channelId}:${userId}`;
      if (typing) {
        setTypingNames((prev) => prev.includes(username) ? prev : [...prev, username]);
        if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
        typingTimers.current[key] = setTimeout(() => {
          setTypingNames((prev) => prev.filter((n) => n !== username));
        }, 3000);
      } else {
        setTypingNames((prev) => prev.filter((n) => n !== username));
      }
    }
    function onChannelBump({ channelId, lastMessageAt }) {
      setChannels((cs) => cs.map((c) => String(c._id) === String(channelId) ? { ...c, lastMessageAt } : c));
    }
    function onReplyBump({ channelId, parentId, lastReplyAt }) {
      if (String(channelId) !== String(activeIdRef.current)) return;
      setMessages((prev) => prev.map((m) => String(m._id) === String(parentId)
        ? { ...m, replyCount: (m.replyCount || 0) + 1, lastReplyAt }
        : m));
    }
    function onLinkPreview({ channelId, messageId, linkPreview }) {
      if (String(channelId) !== String(activeIdRef.current)) return;
      setMessages((prev) => prev.map((m) => String(m._id) === String(messageId) ? { ...m, linkPreview } : m));
    }

    socket.on("presence:initial", onInitialPresence);
    socket.on("presence:online", onOnline);
    socket.on("presence:offline", onOffline);
    socket.on("message:new", onMessage);
    socket.on("message:edit", onMessageEdit);
    socket.on("message:delete", onMessageDelete);
    socket.on("message:reaction", onReaction);
    socket.on("message:pin", onPin);
    socket.on("message:poll_update", onPollUpdate);
    socket.on("message:reply_bump", onReplyBump);
    socket.on("message:link_preview", onLinkPreview);
    socket.on("channel:typing", onTyping);
    socket.on("channel:bump", onChannelBump);
    return () => {
      socket.off("presence:initial", onInitialPresence);
      socket.off("presence:online", onOnline);
      socket.off("presence:offline", onOffline);
      socket.off("message:new", onMessage);
      socket.off("message:edit", onMessageEdit);
      socket.off("message:delete", onMessageDelete);
      socket.off("message:reaction", onReaction);
      socket.off("message:pin", onPin);
      socket.off("message:poll_update", onPollUpdate);
      socket.off("message:reply_bump", onReplyBump);
      socket.off("message:link_preview", onLinkPreview);
      socket.off("channel:typing", onTyping);
      socket.off("channel:bump", onChannelBump);
      socket.off("disconnect", onDisconnect);
      socket.off("reconnect_attempt", onReconnectAttempt);
      socket.off("connect", onConnect);
    };
    // eslint-disable-next-line
  }, []);

  // Read receipts: load readers for active channel + poll
  useEffect(() => {
    if (!activeId) { setReaders([]); return; }
    let cancelled = false;
    async function loadReaders() {
      try {
        const d = await api(`/chat/channels/${activeId}/readers`);
        if (!cancelled) setReaders(d.readers || []);
      } catch (e) {}
    }
    loadReaders();
    const t = setInterval(loadReaders, 30000);
    function onFocus() { loadReaders(); }
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [activeId]);

  function seenByForMessage(m) {
    if (!m || !m.createdAt) return [];
    const created = new Date(m.createdAt).getTime();
    return readers.filter((r) => {
      if (!r.lastSeenAt) return false;
      if (String(r.user?._id) === String(meId)) return false;
      return new Date(r.lastSeenAt).getTime() >= created;
    });
  }

  useEffect(() => {
    if (!activeId) return;
    const socket = getSocket();
    if (socket) {
      socket.emit("channel:join", { channelId: activeId });
      socket.emit("channel:seen", { channelId: activeId });
    }
    setTypingNames([]);
    setPendingAttachments([]);
    loadMessages(activeId);
    setChannels((cs) => cs.map((c) => String(c._id) === String(activeId) ? { ...c, unread: 0 } : c));
    return () => { if (socket) socket.emit("channel:leave", { channelId: activeId }); };
    // eslint-disable-next-line
  }, [activeId]);

  function selectChannel(id) { setActiveId(id); setParams({ c: id }); }

  // ---------- Composer actions ----------
  async function send() {
    const t = text.trim();
    if (!t && pendingAttachments.length === 0) return;
    if (!activeId) return;
    setText("");
    const attachments = pendingAttachments.map((f) => f._id);
    setPendingAttachments([]);
    try {
      await api(`/chat/channels/${activeId}/messages`, {
        method: "POST",
        body: JSON.stringify({ text: t, attachments }),
      });
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }
  function handleTyping() {
    const socket = getSocket();
    if (!socket || !activeId) return;
    socket.emit("channel:typing", { channelId: activeId, typing: true });
    if (composerTypingRef.current) clearTimeout(composerTypingRef.current);
    composerTypingRef.current = setTimeout(() => {
      socket.emit("channel:typing", { channelId: activeId, typing: false });
    }, 2000);
  }
  async function startDm(userId) {
    try {
      const d = await api(`/chat/dm/${userId}`, { method: "POST" });
      await loadChannels();
      setActiveId(d.channel._id); setParams({ c: d.channel._id });
      dmModal.onClose();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function uploadFile(file) {
    const tempId = `tmp_${Math.random().toString(36).slice(2)}`;
    setUploadingFiles((u) => ({ ...u, [tempId]: 0 }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (activeId) fd.append("channelId", activeId);
      const token = localStorage.getItem("token");
      const xhr = new XMLHttpRequest();
      const promise = new Promise((resolve, reject) => {
        xhr.open("POST", `${API_BASE}/files/upload`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadingFiles((u) => ({ ...u, [tempId]: Math.round((e.loaded / e.total) * 100) }));
        };
        xhr.onload = () => {
          let body = {};
          try { body = JSON.parse(xhr.responseText); } catch (e) {}
          if (xhr.status >= 200 && xhr.status < 300) resolve(body);
          else reject(new Error(body.message || `HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(fd);
      });
      const r = await promise;
      setPendingAttachments((p) => [...p, r.file]);
      setUploadingFiles((u) => { const c = { ...u }; delete c[tempId]; return c; });
    } catch (e) {
      toast({ title: e.message || "Upload failed", status: "error" });
      setUploadingFiles((u) => { const c = { ...u }; delete c[tempId]; return c; });
    }
  }

  function onPickFiles(e) {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    e.target.value = "";
  }

  async function createPoll(payload) {
    if (!activeId) return;
    try {
      await api(`/chat/channels/${activeId}/messages`, {
        method: "POST",
        body: JSON.stringify({ poll: payload }),
      });
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function toggleReaction(msgId, emoji) {
    setReactionMenuFor(null);
    try { await api(`/chat/messages/${msgId}/reactions`, { method: "POST", body: JSON.stringify({ emoji }) }); }
    catch (e) {}
  }
  async function deleteMsg(msgId) {
    if (!window.confirm("Delete this message?")) return;
    try { await api(`/chat/messages/${msgId}`, { method: "DELETE" }); } catch (e) {}
  }
  async function togglePin(msg) {
    try { await api(`/chat/messages/${msg._id}/pin`, { method: msg.pinned ? "DELETE" : "POST" }); }
    catch (e) {}
  }
  async function commitEdit() {
    if (!editingId) return;
    const t = editText.trim();
    if (!t) return;
    try {
      await api(`/chat/messages/${editingId}`, { method: "PATCH", body: JSON.stringify({ text: t }) });
      setEditingId(null); setEditText("");
    } catch (e) {}
  }

  // ---------- Sidebar grouping ----------
  const generalChs = channels.filter((c) => c.kind === "general");
  const projectGroups = useMemo(() => {
    const groups = new Map();
    for (const c of channels) {
      if (c.kind === "project") {
        const k = String(c.project?._id || c._id);
        const g = groups.get(k) || { project: c.project, parent: c, depts: [] };
        g.parent = c;
        groups.set(k, g);
      } else if (c.kind === "dept" && c.project) {
        const k = String(c.project._id || c.project);
        const g = groups.get(k) || { project: c.project, parent: null, depts: [] };
        g.depts.push(c);
        groups.set(k, g);
      }
    }
    return Array.from(groups.values()).sort((a, b) => {
      const aT = a.parent?.lastMessageAt || 0;
      const bT = b.parent?.lastMessageAt || 0;
      return new Date(bT) - new Date(aT);
    });
  }, [channels]);
  const dmChs = channels.filter((c) => c.kind === "dm");
  const taskChs = channels.filter((c) => c.kind === "task");

  function toggleProjectCollapse(pid) {
    setCollapsedProjects((s) => {
      const n = new Set(s);
      if (n.has(pid)) n.delete(pid); else n.add(pid);
      return n;
    });
  }

  return (
    <Box>
      {connectionState !== "connected" && (
        <div className={`text-center text-xs px-3 py-1.5 ${connectionState === "reconnecting" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
          {connectionState === "reconnecting" ? "🔄 Reconnecting…" : "⚠️ Disconnected — messages won't sync until reconnected"}
        </div>
      )}
      <div className="flex bg-surface rounded-md shadow-sm overflow-hidden" style={{ height: "calc(100vh - 120px)" }}>
        {/* SIDEBAR */}
        <div className="w-72 border-r border-ink-100 overflow-y-auto bg-ink-50/30">
          <div className="p-3 border-b border-ink-100 bg-surface">
            <button
              onClick={searchDisc.onOpen}
              className="w-full flex items-center gap-2 pl-3 pr-2 py-1.5 text-sm bg-ink-50 border border-transparent rounded outline-none hover:bg-ink-100 text-ink-500"
            >
              <SearchIcon />
              <span className="flex-1 text-left">Search messages…</span>
              <kbd className="text-[9px] bg-surface border border-ink-200 px-1 py-0.5 rounded text-ink-500 font-mono">⌘K</kbd>
            </button>
          </div>

          <Section title="Channels">
            {generalChs.map((c) => (
              <ChannelRow key={c._id} ch={c} active={String(c._id) === String(activeId)} onClick={() => selectChannel(c._id)} meId={meId} onlineSet={onlineSet} />
            ))}
          </Section>

          <Section title={`Projects (${projectGroups.length})`}>
            {projectGroups.length === 0 && <div className="px-4 py-2 text-xs text-ink-400">No project chats yet</div>}
            {projectGroups.map((g) => {
              const pid = String(g.project?._id || g.parent?._id);
              const collapsed = collapsedProjects.has(pid);
              const totalUnread = (g.parent?.unread || 0) + g.depts.reduce((s, d) => s + (d.unread || 0), 0);
              return (
                <div key={pid}>
                  <div className="flex items-center px-3 py-1 group">
                    <button onClick={() => toggleProjectCollapse(pid)} className="text-[10px] text-ink-400 mr-1 px-1 hover:text-ink-600">
                      {collapsed ? "▸" : "▾"}
                    </button>
                    {g.parent ? (
                      <button
                        onClick={() => selectChannel(g.parent._id)}
                        className={[
                          "flex-1 flex items-center gap-1.5 text-xs font-semibold py-1 px-1 rounded text-left",
                          String(g.parent._id) === String(activeId) ? "bg-blue-50 text-blue-700" : "text-ink-700 hover:bg-ink-100",
                        ].join(" ")}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: g.project?.color || "#94a3b8" }} />
                        <span className="truncate">{g.project?.name || "Project"}</span>
                        {totalUnread > 0 && <span className="ml-auto px-1.5 py-0.5 bg-red-500 text-white text-[9px] rounded-full font-mono">{totalUnread}</span>}
                      </button>
                    ) : (
                      <span className="flex-1 text-xs font-semibold text-ink-700 truncate">{g.project?.name || "Project"}</span>
                    )}
                  </div>
                  {!collapsed && g.depts.map((d) => (
                    <ChannelRow key={d._id} ch={d} active={String(d._id) === String(activeId)} onClick={() => selectChannel(d._id)} meId={meId} onlineSet={onlineSet} indent />
                  ))}
                </div>
              );
            })}
          </Section>

          <Section title="Direct Messages" action={
            <button onClick={() => dmModal.onOpen()} className="text-xs text-ink-500 hover:text-ink-700">+</button>
          }>
            {dmChs.length === 0 && <div className="px-4 py-2 text-xs text-ink-400">No DMs yet</div>}
            {dmChs.map((c) => (
              <ChannelRow key={c._id} ch={c} active={String(c._id) === String(activeId)} onClick={() => selectChannel(c._id)} meId={meId} onlineSet={onlineSet} />
            ))}
          </Section>

          {taskChs.length > 0 && (
            <Section title="Task chats">
              {taskChs.slice(0, 10).map((c) => (
                <ChannelRow key={c._id} ch={c} active={String(c._id) === String(activeId)} onClick={() => selectChannel(c._id)} meId={meId} onlineSet={onlineSet} />
              ))}
            </Section>
          )}
        </div>

        {/* MAIN */}
        <div className="flex-1 flex flex-col min-w-0">
          {!activeChannel ? (
            <div className="flex-1 flex items-center justify-center text-ink-400">Select a channel to start chatting.</div>
          ) : (
            <>
              <ChannelHeader
                channel={activeChannel}
                meId={meId}
                onlineSet={onlineSet}
                navigate={navigate}
                muteMode={channelPrefs[activeChannel._id] || "all"}
                onOpenInfo={() => infoDisc.onOpen()}
              />

              {pinned.length > 0 && (
                <div className="border-b border-ink-100 bg-amber-50">
                  <button onClick={() => setShowPinned((s) => !s)} className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100">
                    <span>📌 Pinned ({pinned.length})</span>
                    <span>{showPinned ? "▾" : "▸"}</span>
                  </button>
                  {showPinned && (
                    <ul className="px-3 pb-2 space-y-1 max-h-32 overflow-y-auto">
                      {pinned.map((p) => (
                        <li key={p._id} className="text-xs text-ink-700 bg-surface rounded px-2 py-1 border border-amber-200 flex items-start gap-2">
                          <span className="text-[10px] font-semibold text-ink-500 flex-shrink-0">{p.author?.username || "—"}</span>
                          <span className="flex-1 whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: p.text }} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-3 bg-ink-50/30 space-y-2">
                {loadingMessages ? (
                  <div className="text-center text-xs text-ink-400 py-8">Loading…</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-xs text-ink-400 py-8">No messages yet — say hi 👋</div>
                ) : messages.map((m, i) => {
                  const prev = messages[i - 1];
                  const sameAuthor = prev && !prev.deletedAt && String(prev.author?._id) === String(m.author?._id) &&
                    new Date(m.createdAt) - new Date(prev.createdAt) < 5 * 60 * 1000;
                  const mine = String(m.author?._id || m.author) === String(meId);
                  const isEditing = editingId === m._id;

                  if (m.deletedAt) {
                    return (
                      <div key={m._id} className="flex gap-2 items-start">
                        <div className="w-7 flex-shrink-0" />
                        <div className="text-[11px] text-ink-400 italic">Message deleted</div>
                      </div>
                    );
                  }

                  return (
                    <div key={m._id} className="group flex gap-2 items-start" onMouseEnter={() => setHoverId(m._id)} onMouseLeave={() => { setHoverId(null); setReactionMenuFor(null); }}>
                      <div className="w-7 flex-shrink-0">
                        {!sameAuthor && (
                          <RouterLink to={`/users/${m.author?._id}`}>
                            <div className="w-7 h-7 rounded-full bg-ink-200 text-ink-600 text-[10px] font-semibold flex items-center justify-center hover:ring-2 hover:ring-blue-200 transition" title={m.author?.username}>
                              {(m.author?.username || "?").charAt(0).toUpperCase()}
                            </div>
                          </RouterLink>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 relative">
                        {!sameAuthor && (
                          <div className="flex items-center gap-2 mb-0.5">
                            <RouterLink to={`/users/${m.author?._id}`} className="text-xs font-semibold text-ink-700 hover:underline">{m.author?.username || "—"}</RouterLink>
                            <span className="text-[10px] text-ink-400">{fmtTime(m.createdAt)}</span>
                            {m.pinned && <span className="text-[10px] text-amber-600">📌</span>}
                          </div>
                        )}

                        {m.kind === "poll" ? (
                          <PollMessage message={m} onUpdate={(poll) => setMessages((prev) => prev.map((x) => x._id === m._id ? { ...x, poll } : x))} />
                        ) : isEditing ? (
                          <div className="flex items-center gap-2">
                            <input autoFocus value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") { setEditingId(null); setEditText(""); } }} className="flex-1 text-sm border border-blue-300 rounded px-2 py-1 outline-none" />
                            <button onClick={commitEdit} className="text-xs text-blue-600 hover:underline">Save</button>
                            <button onClick={() => { setEditingId(null); setEditText(""); }} className="text-xs text-ink-500 hover:underline">Cancel</button>
                          </div>
                        ) : (
                          <>
                            {m.text && <div className="text-sm text-ink-800 whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: m.text }} />}
                            <Attachments files={m.attachments} />
                            {m.linkPreview?.url && (
                              <a
                                href={m.linkPreview.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1.5 flex gap-3 items-stretch max-w-[420px] border-l-2 border-blue-400 bg-blue-50/40 hover:bg-blue-50 rounded-r-md p-2 transition"
                                title={m.linkPreview.url}
                              >
                                {m.linkPreview.image && (
                                  <img
                                    src={m.linkPreview.image}
                                    alt=""
                                    className="w-16 h-16 rounded object-cover flex-shrink-0"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  {m.linkPreview.siteName && (
                                    <div className="text-[10px] text-ink-400 uppercase tracking-wider mb-0.5 truncate">{m.linkPreview.siteName}</div>
                                  )}
                                  <div className="text-xs font-semibold text-ink-800 truncate">{m.linkPreview.title}</div>
                                  {m.linkPreview.description && (
                                    <div className="text-[11px] text-ink-500 line-clamp-2 mt-0.5">{m.linkPreview.description}</div>
                                  )}
                                </div>
                              </a>
                            )}
                          </>
                        )}
                        {m.editedAt && !isEditing && <span className="text-[10px] text-ink-400 ml-1">(edited)</span>}

                        {(m.reactions || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {m.reactions.map((r) => {
                              const ours = r.users.some((u) => String(u._id || u) === String(meId));
                              return (
                                <button key={r.emoji} onClick={() => toggleReaction(m._id, r.emoji)}
                                  className={["px-1.5 py-0.5 rounded-full text-[11px] flex items-center gap-1 transition border",
                                    ours ? "bg-blue-100 border-blue-300" : "bg-ink-100 border-ink-200 hover:bg-ink-200"].join(" ")}>
                                  <span>{r.emoji}</span><span className="text-ink-600">{r.users.length}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {(m.replyCount || 0) > 0 && (
                          <button
                            onClick={() => { setOpenThreadParent(m._id); threadDisc.onOpen(); }}
                            className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] text-blue-600 hover:bg-blue-50 border border-blue-200 hover:border-blue-300"
                          >
                            💬 {m.replyCount} {m.replyCount === 1 ? "reply" : "replies"}
                          </button>
                        )}

                        {mine && !m.deletedAt && (() => {
                          const seers = seenByForMessage(m);
                          if (!seers.length) return null;
                          const names = seers.map((s) => s.user?.username || "?").join(", ");
                          return (
                            <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-ink-400" title={`Seen by ${names}`}>
                              <span className="text-emerald-500">✓✓</span>
                              <span>Seen by {seers.length}</span>
                            </div>
                          );
                        })()}

                        {hoverId === m._id && !isEditing && (
                          <div className="absolute -top-3 right-0 bg-surface border border-ink-200 rounded-md shadow-md flex items-center gap-0.5 px-1 py-0.5 z-10">
                            <button onClick={() => setReactionMenuFor(reactionMenuFor === m._id ? null : m._id)} className="px-1.5 py-0.5 text-xs hover:bg-ink-100 rounded" title="React">😊</button>
                            <button onClick={() => { setOpenThreadParent(m._id); threadDisc.onOpen(); }} className="px-1.5 py-0.5 text-xs hover:bg-ink-100 rounded" title="Reply in thread">💬</button>
                            <button onClick={() => togglePin(m)} className="px-1.5 py-0.5 text-xs hover:bg-ink-100 rounded" title={m.pinned ? "Unpin" : "Pin"}>📌</button>
                            {mine && m.kind !== "poll" && (
                              <button onClick={() => { setEditingId(m._id); setEditText((m.text || "").replace(/<[^>]+>/g, "")); }} className="px-1.5 py-0.5 text-xs hover:bg-ink-100 rounded" title="Edit">✏️</button>
                            )}
                            {(mine || localStorage.getItem("role") === "SUPER_ADMIN") && (
                              <button onClick={() => deleteMsg(m._id)} className="px-1.5 py-0.5 text-xs hover:bg-red-50 hover:text-red-600 rounded" title="Delete">🗑️</button>
                            )}
                          </div>
                        )}
                        {reactionMenuFor === m._id && (
                          <div className="absolute top-5 right-0 bg-surface border border-ink-200 rounded-md shadow-md flex gap-0.5 px-1 py-1 z-20">
                            {QUICK_REACTIONS.map((e) => (
                              <button key={e} onClick={() => toggleReaction(m._id, e)} className="px-1 py-0.5 hover:bg-ink-100 rounded text-base">{e}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Typing */}
              <div className="px-3 py-1 h-5 text-[10px] text-ink-400 border-t border-ink-100 bg-ink-50/40">
                {typingNames.length > 0 && `${typingNames.join(", ")} ${typingNames.length === 1 ? "is" : "are"} typing…`}
              </div>

              {/* Pending attachments preview */}
              {(pendingAttachments.length > 0 || Object.keys(uploadingFiles).length > 0) && (
                <div className="px-3 py-2 border-t border-ink-100 bg-ink-50 flex flex-wrap gap-2">
                  {pendingAttachments.map((f) => (
                    <div key={f._id} className="flex items-center gap-1.5 bg-surface border border-ink-200 rounded px-2 py-1 text-xs">
                      <span>📎</span>
                      <span className="truncate max-w-[140px]">{f.name}</span>
                      <button onClick={() => setPendingAttachments((p) => p.filter((x) => x._id !== f._id))} className="text-ink-400 hover:text-red-500">×</button>
                    </div>
                  ))}
                  {Object.entries(uploadingFiles).map(([id, pct]) => (
                    <div key={id} className="flex items-center gap-1.5 bg-surface border border-blue-200 rounded px-2 py-1 text-xs">
                      <span>⬆️</span><span>Uploading {pct}%</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Composer */}
              <div className="p-2 border-t border-ink-100 bg-surface relative">
                {emojiOpen && (
                  <div className="absolute bottom-14 right-2 z-30">
                    <EmojiPicker
                      onEmojiClick={(emojiData) => { setText((t) => t + emojiData.emoji); setEmojiOpen(false); }}
                      width={360}
                      height={420}
                      previewConfig={{ showPreview: false }}
                      searchDisabled={false}
                    />
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onPickFiles} />
                  <button onClick={() => fileInputRef.current?.click()} className="px-2 py-1.5 text-base hover:bg-ink-100 rounded" title="Attach files">📎</button>
                  <button onClick={pollModal.onOpen} className="px-2 py-1.5 text-base hover:bg-ink-100 rounded" title="Create poll">📊</button>
                  <button onClick={() => setEmojiOpen((s) => !s)} className="px-2 py-1.5 text-base hover:bg-ink-100 rounded" title="Emoji">😊</button>
                  <input
                    value={text}
                    onChange={(e) => { setText(e.target.value); handleTyping(); }}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={`Message ${channelLabel(activeChannel, meId)}`}
                    className="flex-1 text-sm border border-ink-200 rounded px-3 py-1.5 outline-none focus:border-blue-300"
                  />
                  <button onClick={send} disabled={!text.trim() && pendingAttachments.length === 0}
                    className={`px-3 py-1.5 rounded text-sm font-semibold text-white ${(text.trim() || pendingAttachments.length) ? "bg-blue-500 hover:bg-blue-600" : "bg-ink-300 cursor-not-allowed"}`}>
                    Send
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* DM picker */}
      <Modal isOpen={dmModal.isOpen} onClose={dmModal.onClose}>
        <ModalOverlay /><ModalContent>
          <ModalHeader>Start a direct message</ModalHeader><ModalCloseButton />
          <ModalBody maxH="400px" overflowY="auto">
            <VStack align="stretch" spacing={1}>
              {users.map((u) => (
                <HStack key={u._id} p={2} _hover={{ bg: "gray.50" }} rounded="md" cursor="pointer" onClick={() => startDm(u._id)}>
                  <Avatar size="sm" name={u.username}>
                    {onlineSet.has(String(u._id)) && <AvatarBadge boxSize="0.9em" bg="green.400" />}
                  </Avatar>
                  <Box flex="1">
                    <Text fontSize="sm" fontWeight={600}>{u.username}</Text>
                    <Text fontSize="xs" color="gray.500">{u.email}</Text>
                  </Box>
                  {u.team && <Tag size="sm" bg={u.team.color || "gray.300"} color="white">{u.team.name}</Tag>}
                </HStack>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter><button onClick={dmModal.onClose} className="px-3 py-1 text-sm hover:bg-ink-100 rounded">Close</button></ModalFooter>
        </ModalContent>
      </Modal>

      <PollCreateModal isOpen={pollModal.isOpen} onClose={pollModal.onClose} onCreate={createPoll} />

      <ThreadDrawer
        isOpen={threadDisc.isOpen}
        onClose={() => { threadDisc.onClose(); setOpenThreadParent(null); }}
        parentId={openThreadParent}
        channelId={activeId}
      />

      {activeChannel && (
        <ChannelInfoModal
          isOpen={infoDisc.isOpen}
          onClose={infoDisc.onClose}
          channel={activeChannel}
          currentMode={channelPrefs[activeChannel._id] || "all"}
          onModeChange={(mode) => setChannelPrefs((p) => ({ ...p, [activeChannel._id]: mode }))}
        />
      )}

      <ChatSearchModal
        isOpen={searchDisc.isOpen}
        onClose={searchDisc.onClose}
        onSelectChannel={(chId) => selectChannel(chId)}
      />
    </Box>
  );
}

function Section({ title, children, action }) {
  return (
    <div>
      <div className="px-4 pt-4 pb-1 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{title}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

function ChannelRow({ ch, active, onClick, meId, onlineSet, indent }) {
  let label;
  let onlineDot = false;
  if (ch.kind === "dm") {
    const other = (ch.members || []).find((m) => String(m._id) !== String(meId));
    label = other ? other.username : "DM";
    if (other && onlineSet.has(String(other._id))) onlineDot = true;
  } else if (ch.kind === "general") label = "general";
  else if (ch.kind === "dept") label = ch.name?.replace(/^dept-/, "🎯 ") || "dept";
  else if (ch.kind === "task") label = ch.name || "task";
  else label = ch.name || "channel";

  return (
    <button
      onClick={onClick}
      className={[
        "w-full flex items-center gap-1.5 py-1 text-sm text-left",
        indent ? "pl-9 pr-3" : "px-4",
        active ? "bg-blue-50 border-l-2 border-blue-500" : "border-l-2 border-transparent hover:bg-ink-100",
      ].join(" ")}
    >
      {ch.kind === "dm" ? (
        <span className="relative w-4 h-4 rounded-full bg-ink-300 text-white text-[8px] font-semibold flex items-center justify-center">
          {(label || "?").charAt(0).toUpperCase()}
          {onlineDot && <span className="absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-500 border border-white" />}
        </span>
      ) : (
        <span className="text-[10px] text-ink-500">{ch.kind === "dept" ? "" : "#"}</span>
      )}
      <span className={["flex-1 truncate", ch.unread > 0 ? "font-semibold text-ink-900" : "text-ink-700"].join(" ")}>
        {label}
      </span>
      {ch.unread > 0 && (
        <span className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] rounded-full font-mono">{ch.unread}</span>
      )}
    </button>
  );
}

function ChannelHeader({ channel, meId, navigate, muteMode, onOpenInfo }) {
  const memberCount = (channel.members || []).length;
  const muteIcon = muteMode === "muted" ? "🔕" : muteMode === "mentions" ? "@" : null;

  function HeaderActions() {
    return (
      <div className="flex items-center gap-1.5">
        {muteIcon && <span className="text-xs px-1.5 py-0.5 bg-ink-100 rounded text-ink-600" title={muteMode}>{muteIcon}</span>}
        <button onClick={onOpenInfo} className="px-2 py-1 text-xs border border-ink-200 rounded hover:bg-ink-50" title="Channel details">
          ⓘ Info
        </button>
      </div>
    );
  }

  if (channel.kind === "project") {
    return (
      <div className="px-4 py-3 border-b border-ink-100 flex items-center gap-3">
        <div className="w-2 h-6 rounded" style={{ background: channel.project?.color || "#94a3b8" }} />
        <div className="flex-1 min-w-0">
          <RouterLink to={`/projects/${channel.project?._id}`} className="text-sm font-semibold text-ink-800 hover:text-blue-600">
            {channel.project?.name || channel.name}
          </RouterLink>
          <div className="text-[10px] text-ink-500">Project chat · {memberCount} member{memberCount !== 1 ? "s" : ""}</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(`/projects/${channel.project?._id}`)}
            className="text-xs px-2 py-1 border border-ink-200 rounded hover:bg-ink-50"
            title="Open project workspace"
          >📋 Project</button>
          <button
            onClick={() => navigate(`/tasks?projectId=${channel.project?._id}`)}
            className="text-xs px-2 py-1 border border-ink-200 rounded hover:bg-ink-50"
            title="Open tasks for this project"
          >✓ Tasks</button>
          <button
            onClick={onOpenInfo}
            className="text-xs px-2 py-1 border border-ink-200 rounded hover:bg-ink-50"
            title="Members and settings"
          >👥 {memberCount}</button>
        </div>
        <HeaderActions />
      </div>
    );
  }
  if (channel.kind === "dept") {
    return (
      <div className="px-4 py-3 border-b border-ink-100 flex items-center gap-3">
        <div className="w-2 h-6 rounded bg-purple-400" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink-800">{channel.name}</div>
          <div className="text-[10px] text-ink-500">
            Dept chat · {memberCount} member{memberCount !== 1 ? "s" : ""}
            {channel.project && (
              <> · <RouterLink to={`/projects/${channel.project?._id}`} className="text-blue-600 hover:underline">{channel.project.name}</RouterLink></>
            )}
          </div>
        </div>
        <HeaderActions />
      </div>
    );
  }
  if (channel.kind === "general") {
    return (
      <div className="px-4 py-3 border-b border-ink-100 flex items-center gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink-800"># general</div>
          <div className="text-[10px] text-ink-500">Org-wide. Everyone can read & post.</div>
        </div>
        <HeaderActions />
      </div>
    );
  }
  if (channel.kind === "dm") {
    const other = (channel.members || []).find((m) => String(m._id) !== String(meId));
    return (
      <div className="px-4 py-3 border-b border-ink-100 flex items-center gap-3">
        <div className="w-7 h-7 rounded-full bg-ink-300 text-white text-xs font-semibold flex items-center justify-center">
          {(other?.username || "?").charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <RouterLink to={`/users/${other?._id}`} className="text-sm font-semibold text-ink-800 hover:text-blue-600">
            {other?.username || "DM"}
          </RouterLink>
          <div className="text-[10px] text-ink-500">{other?.email}</div>
        </div>
        <HeaderActions />
      </div>
    );
  }
  return (
    <div className="px-4 py-3 border-b border-ink-100 flex items-center gap-3">
      <div className="flex-1">
        <div className="text-sm font-semibold text-ink-800">{channel.name || "channel"}</div>
      </div>
      <HeaderActions />
    </div>
  );
}
